import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { Mutex } from 'async-mutex';
import { AUTH_BASE_URL } from '@skynet/config-provider';
import { setAccessToken, refreshTokenFailed } from './actions';
import { getTokenPayload, shouldSkipReauth, isAuthTokenStore } from './helpers';
import { selectAccessToken, selectIsEmailConfirmed, selectIsLoggedOut } from './selectors';

const mutex = new Mutex();

const isBaseQueryApi = (obj: unknown): obj is BaseQueryApi => {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'getState' in obj &&
    typeof (obj as { [key: string]: unknown }).getState === 'function' &&
    'dispatch' in obj &&
    typeof (obj as { [key: string]: unknown }).dispatch === 'function'
  );
};

type TokenRefreshResult = { ok: true; token: string } | { ok: false; error: FetchBaseQueryError };

/**
 * Attempts to refresh the access token
 */
const attemptTokenRefresh = async (
  api: BaseQueryApi,
  extraOptions: object = {},
): Promise<TokenRefreshResult> => {
  const refreshQuery = fetchBaseQuery({
    baseUrl: AUTH_BASE_URL,
    credentials: 'include',
  });

  const refreshResult = await refreshQuery(
    { url: '/api/v1/auth/refresh-token', method: 'POST' },
    api,
    extraOptions,
  );

  // process successful response
  if (
    refreshResult.data &&
    typeof refreshResult.data === 'object' &&
    refreshResult.data !== null &&
    'accessToken' in refreshResult.data &&
    typeof refreshResult.data.accessToken === 'string'
  ) {
    api.dispatch(setAccessToken({ accessToken: refreshResult.data.accessToken }));
    return { ok: true, token: refreshResult.data.accessToken };
  }

  // Token refresh failed - dispatch action for dashboard to handle
  const refreshError: FetchBaseQueryError = refreshResult.error ?? {
    status: 'CUSTOM_ERROR',
    error: 'Unexpected refresh response',
  };
  api.dispatch(
    refreshTokenFailed({
      error: refreshError,
      status: refreshError.status,
    }),
  );

  return { ok: false, error: refreshError };
};

/**
 * Ensures the access token is valid, refreshing if necessary.
 * Use this for non-RTK Query contexts like WebSocket connections.
 */
export const ensureAccessToken = async (
  store: { getState: () => unknown; dispatch: BaseQueryApi['dispatch'] },
  accessToken?: string,
) => {
  const isMissing = !accessToken;
  const isExpired = !isMissing && getTokenPayload(accessToken).isTokenExpired;
  if (isMissing || isExpired) {
    // Create a minimal BaseQueryApi-compatible object for token refresh
    const refreshApi: BaseQueryApi = {
      ...store,
      signal: new AbortController().signal,
      abort: () => {
        /* no-op for refresh context */
      },
      type: 'query',
      endpoint: 'refreshToken',
      forced: false,
      extra: undefined,
    };
    const result = await attemptTokenRefresh(refreshApi, {});
    return result.ok ? result.token : undefined;
  }
  return accessToken;
};

/**
 * Re-authentication base query wrapper.
 * Handles automatic token refresh when the access token expires.
 */
export const ReauthBaseQuery =
  (
    customBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
  ): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> =>
  async (args, api, extraOptions) => {
    if (!isBaseQueryApi(api)) {
      return { error: { status: 'CUSTOM_ERROR', error: 'Invalid API context' } };
    }

    await mutex.waitForUnlock();

    const state = api.getState();

    if (!isAuthTokenStore(state)) {
      return {
        error: {
          status: 'CUSTOM_ERROR',
          error: 'Store must include authTokenReducer from @skynet/data-auth-api',
        },
      };
    }

    const isEmailConfirmed = selectIsEmailConfirmed(state);
    const isLoggedOut = selectIsLoggedOut(state);

    // If the user is explicitly logged out, abort all authenticated requests immediately.
    // This prevents cache-reset-triggered refetches from reaching the network during the signout flow
    // (e.g. when postSignoutLoader calls resetAllRtkApiStates() while profile components are still mounted).
    if (isLoggedOut && !shouldSkipReauth(args)) {
      return { error: { status: 'CUSTOM_ERROR', error: 'Skipped: user is logged out' } };
    }

    if (!shouldSkipReauth(args)) {
      const accessToken = selectAccessToken(state) ?? '';
      const tokenWasExpired = !accessToken || getTokenPayload(accessToken).isTokenExpired;
      if (tokenWasExpired && !isEmailConfirmed) {
        const refreshResult = await attemptTokenRefresh(api, extraOptions);
        if (!refreshResult.ok) {
          // Refresh failed and the token is still expired. Don't proceed with the
          // original request using an expired token — it would only fail with 401
          // (or FETCH_ERROR if offline) and trigger a confusing error cascade.
          return { error: refreshResult.error };
        }
      }
    }

    let result = await customBaseQuery(args, api, extraOptions);

    // Handle 401 errors with automatic retry after refresh
    if (result.error && result.error.status === 401 && !shouldSkipReauth(args)) {
      if (!mutex.isLocked()) {
        const release = await mutex.acquire();
        try {
          const refreshResult = await attemptTokenRefresh(api, extraOptions);
          if (!refreshResult.ok) {
            // Refresh failed after 401 — token is still invalid
            return { error: refreshResult.error };
          }
          result = await customBaseQuery(args, api, extraOptions);
        } finally {
          release();
        }
      } else {
        await mutex.waitForUnlock();
        const concurrentState = api.getState();
        if (!isAuthTokenStore(concurrentState)) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Store must include authTokenReducer from @skynet/data-auth-api',
            },
          };
        }
        const accessToken = selectAccessToken(concurrentState);
        if (!accessToken || getTokenPayload(accessToken).isTokenExpired) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Token refresh failed (concurrent)',
            },
          };
        }
        result = await customBaseQuery(args, api, extraOptions);
      }
    }

    return result;
  };
